import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { LoadingService } from '../../../services/loading.service';
import { SnackService } from '../../../services/snack.service';
import { AtelierAssignmentTour } from '../atelier-assignments/model/atelier-assignment-tour';
import { AtelierDetailTourItem } from '../atelier-detail/model/atelier-detail-tour-item';
import { AtelierUpdateCheckedIdentifier } from '../atelier-detail/model/atelier-update-checked-identifier';
import { AtelierUpdateCheckedResponse } from '../atelier-detail/model/atelier-update-checked-response';
import { AtelierRecoveryListItem } from '../atelier-recovery/model/atelier-recovery-list-item';
import { Observable } from 'rxjs';
import {
  _get,
  _post,
  _put,
  DeserializeVoid,
} from '../../../services/base.service';
import { SignRecupMethod } from '../../../model/sign/enums/sign-recup-method';

@Injectable({
  providedIn: 'root',
})
export class AtelierControllerService {
  private static BASE_URL = '/atelier';
  private readonly get: <T>(
    path: string,
    deserialize: (response: any) => T
  ) => Observable<T>;
  private readonly put: <T>(
    path: string,
    body: any,
    deserialize: (response: any) => T
  ) => Observable<T>;
  private readonly post: <T>(
    path: string,
    body: RecoverBoardRequestBody | RecoveryListFilterCommand,
    deserialize: (response: any) => T
  ) => Observable<T>;

  private readonly deserializeAssignment = (res) =>
    res as AtelierAssignmentTour[];
  private readonly deserializeAssignmentDetail = (res) =>
    res as AtelierDetailTourItem[];
  private readonly deserializeRecoveryList = (res) =>
    res as AtelierRecoveryListItem[];
  private readonly deserializeUpdateChecked = (res) =>
    res as AtelierUpdateCheckedResponse;
  private readonly deserializeRecoveryPerformanceStats = (res) =>
    res as EmployeeRecoveryStatsDTO[];
  constructor(
    http: HttpClient,
    snackService: SnackService,
    loadingService: LoadingService,
    router: Router
  ) {
    const requestServices = {
      http,
      loadingService,
      router,
      snackService,
    };
    this.get = _get(AtelierControllerService.BASE_URL, requestServices);
    this.put = _put(AtelierControllerService.BASE_URL, requestServices);
    this.post = _post(AtelierControllerService.BASE_URL, requestServices);
  }

  public myAssignments(
    date: Date,
    review: boolean = false
  ): Promise<AtelierAssignmentTour[]> {
    if (review) {
      return this.get(
        `/my-review-assignments?reviewDateUnix=${date.getTime()}`,
        this.deserializeAssignment
      ).toPromise();
    } else {
      return this.get(
        `/my-assignments?atelierDateUnix=${date.getTime()}`,
        this.deserializeAssignment
      ).toPromise();
    }
  }

  public getRecoveryPerformanceStats(
    date: Date
  ): Promise<EmployeeRecoveryStatsDTO[]> {
    return this.get(
      `/recovery/performance?date=${date.getTime()}`,
      this.deserializeRecoveryPerformanceStats
    ).toPromise();
  }

  public assignmentDetail(tourId: number): Promise<AtelierDetailTourItem[]> {
    return this.get(
      `/detail/${tourId}`,
      this.deserializeAssignmentDetail
    ).toPromise();
  }

  public updateChecked(
    tourItemId: number,
    identifier: AtelierUpdateCheckedIdentifier,
    value: boolean,
    onFrame?: boolean
  ): Promise<AtelierUpdateCheckedResponse> {
    const body = {
      tourItemId: tourItemId,
      identifier: identifier,
      value: value,
      onFrame: onFrame,
    };
    return this.put(
      '/update-checked',
      body,
      this.deserializeUpdateChecked
    ).toPromise();
  }

  public recoveryList(
    filterCommand: RecoveryListFilterCommand
  ): Promise<AtelierRecoveryListItem[]> {
    return this.post(
      '/recoveryList',
      filterCommand,
      this.deserializeRecoveryList
    ).toPromise();
  }

  public recoverBoard(
    repoId: number,
    ipBoardPartId: number,
    recoveryAmount: number,
    recupMethod: SignRecupMethod
  ): Promise<void> {
    const requestBody: RecoverBoardRequestBody = {
      repoId: repoId,
      ipBoardPartId: ipBoardPartId,
      recoveryAmount: recoveryAmount,
      recupMethod: recupMethod,
    };
    return this.post(`/recoverBoard`, requestBody, DeserializeVoid).toPromise();
  }
}

interface RecoverBoardRequestBody {
  repoId: number;
  ipBoardPartId: number;
  recoveryAmount: number;
  recupMethod: SignRecupMethod;
}

export interface RecoveryListFilterCommand {
  sortByCompany: boolean;
  companySearchTerm?: string;
  phoneSearchTerm?: string;
  branchSearchTerm?: string;
  dateSearch: Date;
}

export interface EmployeeRecoveryStatsDTO {
  name: string;
  frameCount: number;
  duopropCount: number;
  destroyCount: number;
}
